import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import "@/pages/landing.css";
import {
  Aperture, Sparkles, Camera, Lightbulb, Bookmark, ArrowRight,
  Menu, X, ChevronDown,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1621024994278-e409544f4085?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CTA_IMG = "https://images.unsplash.com/photo-1580744889748-bc1d15b9a847?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FEATURES = [
  { icon: Sparkles, title: "AI Shoot Recipes", desc: "Complete camera settings, lighting steps and composition direction generated in seconds." },
  { icon: Camera, title: "Lens-Aware Intelligence", desc: "Recommendations tuned to your exact focal length — from wide angle to telephoto." },
  { icon: Lightbulb, title: "Lighting Blueprints", desc: "Step-by-step setups for natural light, speedlights, continuous LED or studio strobe." },
  { icon: Bookmark, title: "Save & Revisit", desc: "Bank your favourite combinations as presets and replay your full shoot history." },
];

const STEPS = [
  { n: "1", title: "Choose your gear", desc: "Pick your lens, lighting type and number of lights from three simple dropdowns." },
  { n: "2", title: "Generate", desc: "Claude composes a full, professional shoot recipe tailored to your exact setup." },
  { n: "3", title: "Shoot with confidence", desc: "Dial in the settings, follow the lighting diagram, and create your best work." },
];

function Logo() {
  return (
    <span className="ll-logo">
      <Aperture size={26} strokeWidth={1.6} style={{ color: "var(--color-accent)" }} />
      <span className="ll-logo-word">Aevolux</span>
    </span>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".ll-landing .animate-on-scroll");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("visible"); io.unobserve(e.target); } }),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const primaryCta = user
    ? { to: "/studio", label: "Open Studio" }
    : { to: "/register", label: "Start Free" };

  // Logged-in users are sent straight to the studio.
  if (user) return <Navigate to="/studio" replace />;

  return (
    <div className="ll-landing">
      {/* NAV */}
      <nav className={`ll-nav ${scrolled ? "scrolled" : ""}`} data-testid="landing-nav">
        <div className="ll-wrap ll-nav-inner">
          <Link to="/" data-testid="landing-logo"><Logo /></Link>
          <div className="ll-nav-links">
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
          </div>
          <div className="ll-nav-actions">
            {user ? (
              <Link to="/studio" className="ll-btn ll-btn-primary" data-testid="landing-open-studio">Open Studio</Link>
            ) : (
              <>
                <Link to="/login" className="ll-btn ll-btn-ghost ll-desktop-only" data-testid="landing-signin">Sign in</Link>
                <Link to="/register" className="ll-btn ll-btn-primary" data-testid="landing-cta-nav">Start Free</Link>
              </>
            )}
            <button className="ll-burger" onClick={() => setDrawer((d) => !d)} aria-label="Menu" data-testid="landing-burger">
              {drawer ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <div className={`ll-drawer ${drawer ? "open" : ""}`} onClick={() => setDrawer(false)}>
        <a href="#features">Features</a>
        <a href="#how">How it works</a>
        {!user && <a href="/login">Sign in</a>}
      </div>

      {/* HERO */}
      <header className="ll-hero">
        <div className="ll-hero-bg">
          <img src={HERO_IMG} alt="Photographer composing a shot in a dark studio" loading="eager" fetchPriority="high" />
        </div>
        <div className="ll-wrap ll-hero-content">
          <span className="ll-overline"><span className="dot" style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "var(--color-accent)" }} /> AI Lighting &amp; Lens Assistant</span>
          <h1 className="ll-display">Light, <em>engineered</em>.</h1>
          <p className="ll-hero-sub">Aevolux turns your lens and lighting choices into precise camera settings, lighting diagrams and creative direction — instantly.</p>
          <div className="ll-hero-ctas">
            <Link to={primaryCta.to} className="ll-btn ll-btn-primary" data-testid="hero-primary-cta">{primaryCta.label} <ArrowRight size={16} /></Link>
            <a href="#how" className="ll-btn ll-btn-secondary" data-testid="hero-secondary-cta">See how it works</a>
          </div>
          <div className="ll-proof">
            <span className="dot" /> Trusted by portrait, wedding &amp; editorial shooters
          </div>
        </div>
        <a href="#features" className="ll-scroll-ind" aria-label="Scroll down"><ChevronDown size={28} /></a>
      </header>

      {/* FEATURES */}
      <section className="ll-section" id="features">
        <div className="ll-wrap">
          <div className="ll-section-head animate-on-scroll">
            <span className="ll-overline">Why Aevolux</span>
            <h2 className="ll-display">Every shot, dialled in.</h2>
            <p>Stop guessing at f-stops and light placement. Get a complete, professional recipe for any combination of glass and lighting.</p>
          </div>
          <div className="ll-grid-4">
            {FEATURES.map((f, i) => (
              <article className="ll-feature animate-on-scroll" key={f.title} style={{ transitionDelay: `${i * 60}ms` }} data-testid={`feature-card-${i}`}>
                <div className="ll-feature-icon"><f.icon size={22} strokeWidth={1.6} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="ll-section ll-section--surface" id="how">
        <div className="ll-wrap">
          <div className="ll-section-head animate-on-scroll" style={{ marginInline: "auto", textAlign: "center" }}>
            <span className="ll-overline">How it works</span>
            <h2 className="ll-display">Three steps to a better frame.</h2>
          </div>
          <div className="ll-steps">
            {STEPS.map((s, i) => (
              <div className="ll-step animate-on-scroll" key={s.n} style={{ transitionDelay: `${i * 80}ms` }} data-testid={`step-${i}`}>
                <div className="ll-step-num">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ll-cta">
        <div className="ll-cta-bg"><img src={CTA_IMG} alt="Studio softbox lighting in the dark" loading="lazy" /></div>
        <div className="ll-wrap ll-cta-content animate-on-scroll">
          <span className="ll-overline" style={{ justifyContent: "center" }}>Ready when you are</span>
          <h2 className="ll-display">Your next shoot starts here.</h2>
          <p>Join photographers who plan smarter and shoot with total confidence. Free to start — no credit card required.</p>
          <div className="ll-hero-ctas">
            <Link to={primaryCta.to} className="ll-btn ll-btn-primary" data-testid="final-cta-primary">{primaryCta.label} <ArrowRight size={16} /></Link>
            {!user && <Link to="/login" className="ll-btn ll-btn-secondary" data-testid="final-cta-signin">Sign in</Link>}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ll-footer">
        <div className="ll-wrap">
          <div className="ll-foot-grid">
            <div>
              <Logo />
              <p className="ll-foot-blurb">The AI lighting and lens assistant for photographers who care about every frame.</p>
            </div>
            <div>
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#how">How it works</a></li>
                <li><Link to={user ? "/studio" : "/register"}>Open Studio</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                <li><Link to="/register">Get started</Link></li>
                <li><Link to="/login">Sign in</Link></li>
              </ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><Link to="/privacy" data-testid="footer-privacy">Privacy Policy</Link></li>
                <li><Link to="/terms" data-testid="footer-terms">Terms of Service</Link></li>
                <li><a href="mailto:lawrence@chapter8studio.com">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="ll-foot-bottom">
            <span>© {new Date().getFullYear()} Chapter 8 Studio</span>
            <span>Aevolux — AI Lighting &amp; Lens Assistant</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
